import React from 'react';
import { HardHat, Minus, Plus, X } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Field, Select } from '../ui/Field';

export interface BesoinLigne { extra_type_id: string; needed_count: number }

interface TypeExtra { id: string; label: string; partenaire: string }

/**
 * « De quoi aurez-vous besoin ? » — la saisie du besoin en prestataires, APRÈS le devis signé et AVANT
 * la création de la privatisation (demande d'Emeni, 18/09).
 *
 * LA DISTINCTION QUI COMPTE : ici on note CE QU'IL FAUT (deux hôtesses, un agent), pas QUI vient. Le
 * booking — choisir les personnes, envoyer la demande au partenaire — se fait ensuite depuis le module
 * Extras, avec « Booker » ou « Demander à booker ». Écrire le besoin au moment où l'on fixe la date,
 * c'est le noter quand on l'a en tête : la privatisation naît avec son besoin déjà renseigné, au lieu
 * qu'on y revienne plus tard en se demandant ce qui avait été convenu.
 *
 * Volontairement facultatif : beaucoup de privatisations n'ont besoin d'aucun extra, et l'étape ne doit
 * pas devenir un passage obligé qu'on remplit au hasard pour pouvoir avancer.
 */
export function BesoinPrestatairesField({ lignes, onChange }: {
    lignes: BesoinLigne[];
    onChange: (l: BesoinLigne[]) => void;
}) {
    const [types, setTypes] = React.useState<TypeExtra[] | null>(null);

    React.useEffect(() => {
        moduleApi.getExtrasPartners()
            .then((r: any) => {
                const liste: TypeExtra[] = (Array.isArray(r) ? r : []).flatMap((p: any) =>
                    (p.types || [])
                        // Un type hors périmètre événementiel ne concerne pas une privatisation.
                        .filter((t: any) => t.scope_evenementiel !== false && t.scope_evenementiel !== 0)
                        .map((t: any) => ({ id: t.id, label: t.label, partenaire: p.name })));
                setTypes(liste);
            })
            .catch(() => setTypes([]));
    }, []);

    if (types === null || types.length === 0) return null;

    const dejaPris = new Set(lignes.map((l) => l.extra_type_id));
    const restants = types.filter((t) => !dejaPris.has(t.id));
    const nomDuType = (id: string) => types.find((t) => t.id === id);

    const ajouter = (id: string) => { if (id) onChange([...lignes, { extra_type_id: id, needed_count: 1 }]); };
    const changer = (id: string, delta: number) => onChange(lignes.map((l) =>
        l.extra_type_id === id ? { ...l, needed_count: Math.max(1, l.needed_count + delta) } : l));
    const retirer = (id: string) => onChange(lignes.filter((l) => l.extra_type_id !== id));

    return (
        <Field label="Besoin en prestataires"
            help="Vous notez seulement ce qu'il vous faut. Choisir qui vient se fait ensuite dans Extras, avec « Booker » ou « Demander à booker ».">
            {({ id }) => (
                <div className="space-y-2">
                    {lignes.length > 0 && (
                        <div className="space-y-1.5">
                            {lignes.map((l) => {
                                const t = nomDuType(l.extra_type_id);
                                return (
                                    <div key={l.extra_type_id} className="flex items-center gap-2 px-2.5 py-2 rounded-[10px] border border-border bg-surface-soft">
                                        <HardHat size={13} className="shrink-0 text-content-muted" />
                                        <span className="flex-1 min-w-0 text-[12.5px] text-content truncate">
                                            {t?.label || 'Poste'}
                                            {t?.partenaire ? <span className="text-content-muted"> · {t.partenaire}</span> : null}
                                        </span>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button type="button" onClick={() => changer(l.extra_type_id, -1)} disabled={l.needed_count <= 1}
                                                aria-label="Un de moins"
                                                className="w-[26px] h-[26px] rounded-lg border border-border bg-surface text-content flex items-center justify-center disabled:opacity-40">
                                                <Minus size={12} />
                                            </button>
                                            <span className="w-[26px] text-center font-mono text-[13px] tabular-nums text-content">{l.needed_count}</span>
                                            <button type="button" onClick={() => changer(l.extra_type_id, 1)}
                                                aria-label="Un de plus"
                                                className="w-[26px] h-[26px] rounded-lg border border-border bg-surface text-content flex items-center justify-center">
                                                <Plus size={12} />
                                            </button>
                                            <button type="button" onClick={() => retirer(l.extra_type_id)}
                                                aria-label="Retirer ce poste"
                                                className="w-[26px] h-[26px] ml-0.5 rounded-lg text-content-muted hover:text-state-danger flex items-center justify-center">
                                                <X size={13} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {restants.length > 0 && (
                        // Le select se vide après chaque ajout : il sert à ajouter une ligne, pas à porter
                        // une valeur. Sans ça, le dernier poste ajouté restait affiché comme « choisi ».
                        <Select id={id} value="" onChange={(e) => ajouter(e.target.value)}>
                            <option value="">{lignes.length === 0 ? 'Ajouter un poste…' : 'Ajouter un autre poste…'}</option>
                            {restants.map((t) => (
                                <option key={t.id} value={t.id}>{t.label} · {t.partenaire}</option>
                            ))}
                        </Select>
                    )}
                </div>
            )}
        </Field>
    );
}
